import { ref } from 'vue';
// import seccionesService from '@/services/secciones_service.js'
import { getModuleName } from '@/shared/utils/module_helpers.js';
import elementoTipoService from '@/services/elemento_tipo_service';
import {
  mapaPosicionPorUidProceso,
  materialesOrigenDesdeItemProceso,
} from '@/shared/utils/procesos_precedentes.js';

export function useElementos() {
  const modelo = ref([]);
  const isLoading = ref(false);

  const getFieldsByModule = (modulo) => {
    const modulePrefix = getModuleName(modulo);

    return {
      elementoProceso: `${modulePrefix}ElementoProceso`,
      elementoMateriales: `${modulePrefix}ElementoProcesoMateriales`,
      // elementoTinta: `${modulePrefix}ElementoTinta`,
      producto: `${modulePrefix}Producto`,
    };
  };

  const standarizeElemento = (elemento, seccionesData, modulo) => {
    const fields = getFieldsByModule(modulo);

    const Procesos = !elemento[fields.elementoProceso]
      ? []
      : elemento[fields.elementoProceso].map((p) => {
          const { [fields.elementoMateriales]: materiales, ...rest } = p;

          // Organizar materiales por categoría
          // console.debug("Organizando materiales por categoría para el proceso:", p.Codigo)
          const categorias = (p.Proceso?.ProcesoElementoTipoCategoriaMaterial || []).map((c) => {
            const categoria = c.CategoriaMaterial;
            return {
              ...categoria,
              // COTI-642: flag de la relación proceso↔categoría (habilita la columna "Proceso"
              // en la grilla de materiales). Vive en la rel, no en la categoría.
              ProcesoPrecedente: c.ProcesoPrecedente === true,
              ElementoMateriales: materiales
                ? materiales.filter(
                    (m) => m.Material.MaterialTipo.CodigoMaterialCategoria === categoria.Codigo,
                  )
                : [],
            };
          });

          return {
            ...rest,
            // COTI-635: recurso con el que se calcularon las filas por unidad de este proceso
            // (último guardado). Sólo de cliente: standardToModule lo quita antes del PATCH.
            _CodigoRecursoPersistido: rest.CodigoRecurso ?? rest.Recurso?.Codigo ?? null,
            CategoriasMaterial: categorias,
            ElementoMateriales: materiales || [],
          };
        });

    // const ElementoTinta = !elemento[fields.elementoTinta] ? [] : elemento[fields.elementoTinta].map(t => {
    //   return {
    //     ...t,
    //   }
    // })

    // Obtener secciones específicas para este ElementoTipo
    const Secciones = seccionesData.map((s) => {
      const procesos = Procesos.filter((p) => p.CodigoSeccion === s.Codigo);

      procesos.sort((a, b) => {
        const ordenA = a.Orden || 0;
        const ordenB = b.Orden || 0;
        return ordenA - ordenB;
      });
      return {
        ...s,
        ElementoProceso: procesos,
      };
    });

    // COTI-642: asigna _uid a cada proceso y convierte, en cada fila de material,
    // ItemProcesoOrigen (posición del backend) a ProcesoOrigenUid (_uid cliente), usando el
    // mismo orden aplanado (secciones concatenadas) que consumen input_procesos.vue y
    // standardToModule.
    materialesOrigenDesdeItemProceso(Secciones.flatMap((s) => s.ElementoProceso || []));

    const {
      [fields.elementoProceso]: _ElementoProceso,
      // [fields.elementoTinta]: ElementoTintaOriginal,
      [fields.producto]: Productos,
      ...rest
    } = elemento;

    const elementoStandard = {
      ...rest,
      // ElementoTinta,
      Secciones,
      Productos,
      EtiquetasEje: rest.EtiquetasEje || 1,
      EtiquetasDesarrollo: rest.EtiquetasDesarrollo || 1,
    };

    return elementoStandard;
  };

  const standarizeElementos = (elementos, seccionesPorElementoTipo, modulo) => {
    return elementos.map((elemento) =>
      standarizeElemento(elemento, seccionesPorElementoTipo[elemento.ElementoTipo.Codigo], modulo),
    );
  };

  const standardToModule = (elementoStandard, modulo) => {
    const fields = getFieldsByModule(modulo);

    const { /*ElementoTinta, */ Secciones, Productos, ...rest } = elementoStandard;

    // COTI-642: posición 1-based de cada proceso (para convertir ProcesoOrigenUid de las
    // filas de material a ItemProcesoOrigen). Mismo orden aplanado que el backend.
    const procesosPlanos =
      Secciones?.flatMap((s) => s.ElementoProceso || s.elementoProceso || []) || [];
    const posicionPorUidProceso = mapaPosicionPorUidProceso(procesosPlanos);
    const resolverItemProcesoOrigen = (m) => {
      // Tras estandarizar, `ProcesoOrigenUid` es la fuente de verdad de la fila (lo setea
      // materialesOrigenDesdeItemProceso a string _uid o null). null / '' / undefined = sin
      // proceso origen, incluye el caso "el usuario lo quitó": NO reusar el ItemProcesoOrigen
      // viejo que todavía cuelga del objeto.
      const uid = m?.ProcesoOrigenUid;
      if (typeof uid !== 'string' || uid.length === 0) return null;
      return posicionPorUidProceso.get(uid) ?? null;
    };

    // Extraer procesos de todas las secciones
    const procesos =
      Secciones?.flatMap((seccion) => {
        // Manejar tanto ElementoProceso como elementoProceso para compatibilidad
        const procesosPorSeccion = seccion.ElementoProceso || seccion.elementoProceso || [];
        return procesosPorSeccion.map((proceso) => {
          // Extraer materiales de las categorías o usar ElementoMateriales directo para compatibilidad
          let materialesFinales = [];

          if (proceso.CategoriasMaterial && Array.isArray(proceso.CategoriasMaterial)) {
            // Nuevo formato: extraer materiales de cada categoría
            materialesFinales = proceso.CategoriasMaterial.flatMap(
              (categoria) => categoria.ElementoMateriales || [],
            );
          } else {
            // Formato anterior: usar ElementoMateriales directamente para compatibilidad
            const materiales =
              proceso.ElementoMateriales !== undefined
                ? proceso.ElementoMateriales
                : proceso.elementoMateriales;
            materialesFinales = materiales !== undefined ? materiales : [];
          }

          // COTI-642: `_uid` y `ProcesoOrigenUid` son de cliente; no se envían al backend.
          // COTI-635: `_CodigoRecursoPersistido` tampoco se envía.
          const { _uid, _CodigoRecursoPersistido, ...procesoRest } = proceso;
          void _uid;
          void _CodigoRecursoPersistido;

          // Manejar casos donde materialesFinales puede ser null
          let materialesFormateados;
          if (materialesFinales === null) {
            materialesFormateados = null;
          } else if (Array.isArray(materialesFinales)) {
            materialesFormateados = materialesFinales.map((m) => {
              // COTI-635: las filas por unidad son derivadas y de sólo lectura; no se reenvían.
              const { ProcesoOrigenUid, OrdenProduccionCantidadElementoProcMatUnidad, ...matRest } =
                m;
              void ProcesoOrigenUid;
              void OrdenProduccionCantidadElementoProcMatUnidad;
              return {
                ...matRest,
                CodigoMaterial: m.Material?.Codigo || null,
                // COTI-642: proceso del que proviene el material (posición 1-based).
                ItemProcesoOrigen: resolverItemProcesoOrigen(m),
                // Incluir AtributosAdicionales como objeto (no JSON string)
                AtributosAdicionales: m.AtributosAdicionales || null,
              };
            });
          } else {
            materialesFormateados = [];
          }

          return {
            ...procesoRest,
            CodigoRecurso: procesoRest.Recurso?.Codigo || null,
            [fields.elementoMateriales]: materialesFormateados,
          };
        });
      }) || [];

    const elemento = {
      ...rest,
      [fields.elementoProceso]: procesos,
      // [fields.elementoTinta]: ElementoTinta,
      [fields.producto]: Productos || [],
    };

    delete elemento.Procesos;
    delete elemento.Tintas;

    return elemento;
  };

  const standardToModuleArray = (elementosStandard, modulo) => {
    return elementosStandard.map((elemento) => standardToModule(elemento, modulo));
  };

  const initializeModelo = async (elementos, modulo) => {
    isLoading.value = true;

    // Obtener códigos únicos de ElementoTipo
    const codigosElementoTipo = [
      ...new Set(elementos.map((elemento) => elemento.ElementoTipo.Codigo)),
    ];

    // Realizar llamadas en paralelo para cada código de ElementoTipo
    const promesasSecciones = codigosElementoTipo.map((codigo) =>
      elementoTipoService
        .getSeccionesByElementoTipo(codigo, modulo)
        .then((secciones) => ({ codigo, secciones })),
    );

    // Esperar todas las respuestas y crear índice por código
    const resultadosSecciones = await Promise.all(promesasSecciones);
    const seccionesPorElementoTipo = resultadosSecciones.reduce((acc, { codigo, secciones }) => {
      acc[codigo] = secciones;
      return acc;
    }, {});

    //TODO: en lugar de buscar todas las secciones solo obtener las que se definieron en RelProcesoElementoTipo
    // const seccionesData = await seccionesService.findAll()
    modelo.value = standarizeElementos(elementos, seccionesPorElementoTipo, modulo);

    isLoading.value = false;
    return modelo.value;
  };

  return {
    modelo,
    isLoading,
    standarizeElemento,
    standarizeElementos,
    standardToModule,
    standardToModuleArray,
    initializeModelo,
    getFieldsByModule,
  };
}
